import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { X, User, Palette, Globe, Save } from 'lucide-react';
import { toast } from 'sonner';
import i18n from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'ro', name: 'Romana' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Francais' },
  { code: 'es', name: 'Espanol' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Portugues' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'pl', name: 'Polski' },
  { code: 'ru', name: 'Pусский' },
  { code: 'uk', name: 'Yкраїнська' },
  { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' },
  { code: 'zh', name: '中文' },
  { code: 'ar', name: 'العربية' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'tr', name: 'Turkce' },
  { code: 'sv', name: 'Svenska' },
  { code: 'no', name: 'Norsk' },
  { code: 'da', name: 'Dansk' },
  { code: 'fi', name: 'Suomi' },
  { code: 'el', name: 'Ελληνικά' },
  { code: 'cs', name: 'Cestina' },
  { code: 'hu', name: 'Magyar' },
  { code: 'bg', name: 'Български' },
  { code: 'hr', name: 'Hrvatski' },
  { code: 'sk', name: 'Slovencina' },
  { code: 'sl', name: 'Slovenscina' },
  { code: 'lt', name: 'Lietuviu' },
  { code: 'lv', name: 'Latviesu' },
  { code: 'et', name: 'Eesti' },
  { code: 'th', name: 'ไทย' },
  { code: 'vi', name: 'Tieng Viet' },
  { code: 'id', name: 'Bahasa Indonesia' },
  { code: 'ms', name: 'Bahasa Melayu' },
];

const THEME_PRESETS = [
  { name: 'Default Dark', primary: '190 80% 45%', bg: '220 15% 4%', accent: '220 10% 12%' },
  { name: 'Discord', primary: '235 86% 65%', bg: '220 7% 20%', accent: '220 7% 24%' },
  { name: 'Emerald', primary: '160 84% 39%', bg: '160 10% 4%', accent: '160 8% 10%' },
  { name: 'Amber', primary: '38 92% 50%', bg: '30 10% 4%', accent: '30 8% 10%' },
  { name: 'Rose', primary: '350 89% 60%', bg: '350 10% 4%', accent: '350 8% 10%' },
  { name: 'Violet', primary: '270 70% 60%', bg: '270 10% 4%', accent: '270 8% 10%' },
  { name: 'Ocean', primary: '200 80% 50%', bg: '200 20% 4%', accent: '200 15% 10%' },
  { name: 'Sunset', primary: '15 80% 55%', bg: '15 10% 4%', accent: '15 8% 10%' },
];

export const UserSettings = ({ onClose }) => {
  const { user, token, updateProfile } = useAuth();
  const { t } = useTranslation();
  const [section, setSection] = useState('profile');
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [saving, setSaving] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(() => {
    return localStorage.getItem('wg_theme') || 'Default Dark';
  });
  const [language, setLanguage] = useState(user?.language || i18n.language || 'en');

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile({ display_name: displayName, bio });
      toast.success('Profile updated!');
    } catch (e) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleThemeChange = (theme) => {
    setSelectedTheme(theme.name);
    localStorage.setItem('wg_theme', theme.name);
    const root = document.documentElement;
    root.style.setProperty('--primary', theme.primary);
    toast.success(`Theme: ${theme.name}`);
  };

  const handleLanguageChange = async (code) => {
    setLanguage(code);
    i18n.changeLanguage(code);
    localStorage.setItem('i18nextLng', code);
    try {
      await fetch(`${API_URL}/api/users/me/language?language=${code}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { console.error(e); }
  };

  const sections = [
    { id: 'profile', label: t('app.profile'), icon: User },
    { id: 'appearance', label: t('app.appearance'), icon: Palette },
    { id: 'language', label: t('app.language'), icon: Globe },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-[#0b0d10] flex" data-testid="user-settings">
      {/* Sidebar */}
      <div className="w-56 bg-[#0e1015] border-r border-white/5 flex flex-col">
        <div className="h-12 px-4 flex items-center border-b border-white/5">
          <span className="text-sm font-semibold text-white">{t('app.userSettings')}</span>
        </div>
        <ScrollArea className="flex-1 px-2 py-2">
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              data-testid={`settings-${s.id}`}
              className={`flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm transition-colors mb-0.5 ${
                section === s.id ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <s.icon className="h-4 w-4" />
              {s.label}
            </button>
          ))}
        </ScrollArea>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header with close */}
        <div className="h-12 px-6 flex items-center justify-between border-b border-white/5 flex-shrink-0">
          <h2 className="text-sm font-semibold text-white">{sections.find(s => s.id === section)?.label}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1 rounded hover:bg-white/10 transition-colors" data-testid="close-settings">
            <X className="h-5 w-5" />
          </button>
        </div>

        <ScrollArea className="flex-1 p-6">
          {section === 'profile' && (
            <div className="max-w-lg space-y-6">
              {/* Avatar preview */}
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-2xl">{(user?.display_name || user?.username || '?')[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-lg font-semibold text-white">{user?.display_name || user?.username}</p>
                  <p className="text-sm text-zinc-500">@{user?.username}</p>
                </div>
              </div>

              <Separator className="bg-white/5" />

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-zinc-300 uppercase tracking-wide">{t('auth.displayName')}</Label>
                <Input
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  className="bg-[#12141a] border-white/5 text-sm"
                  data-testid="settings-display-name"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-zinc-300 uppercase tracking-wide">{t('app.bio')}</Label>
                <Textarea
                  value={bio}
                  onChange={e => setBio(e.target.value)}
                  placeholder="Tell us about yourself..."
                  className="bg-[#12141a] border-white/5 text-sm resize-none h-24"
                  data-testid="settings-bio"
                />
              </div>

              <Button onClick={handleSaveProfile} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-profile">
                <Save className="h-4 w-4 mr-2" />
                {t('app.save')}
              </Button>
            </div>
          )}

          {section === 'appearance' && (
            <div className="max-w-lg space-y-6">
              <p className="text-sm text-zinc-400 mb-4">{t('app.theme')}</p>
              <div className="grid grid-cols-2 gap-3">
                {THEME_PRESETS.map(theme => {
                  const [h, s, l] = theme.primary.split(' ');
                  return (
                    <button
                      key={theme.name}
                      onClick={() => handleThemeChange(theme)}
                      data-testid={`theme-${theme.name.toLowerCase().replace(/\s/g, '-')}`}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                        selectedTheme === theme.name
                          ? 'border-cyan-500/50 bg-white/5'
                          : 'border-white/5 hover:border-white/10 hover:bg-white/[0.02]'
                      }`}
                    >
                      <div
                        className="h-8 w-8 rounded-full flex-shrink-0"
                        style={{ backgroundColor: `hsl(${theme.primary})` }}
                      />
                      <span className="text-sm text-zinc-200">{theme.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {section === 'language' && (
            <div className="max-w-lg space-y-4">
              <p className="text-sm text-zinc-400 mb-4">{t('app.language')}</p>
              <div className="grid grid-cols-2 gap-2">
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    data-testid={`lang-${lang.code}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                      language === lang.code
                        ? 'bg-cyan-600/20 border border-cyan-500/30 text-white'
                        : 'bg-[#12141a] border border-white/5 text-zinc-400 hover:text-white hover:border-white/10'
                    }`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};
